import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

// Auth endpoints that should NOT trigger token refresh on 401
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/me'];

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Refs for interceptor logic
  const isRefreshing = useRef(false);
  const failedQueue = useRef([]);
  const interceptorsSetup = useRef(false);

  // Process queued requests after token refresh
  const processQueue = useCallback((error, newToken = null) => {
    failedQueue.current.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(newToken);
      }
    });
    failedQueue.current = [];
  }, []);

  // Setup interceptor once
  useEffect(() => {
    if (interceptorsSetup.current) return;
    interceptorsSetup.current = true;

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // Check if this is a 401 error
        if (error.response?.status !== 401) {
          return Promise.reject(error);
        }

        // Check if this is an auth endpoint - don't retry these
        const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => 
          originalRequest.url?.includes(endpoint)
        );
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }

        // Check if already retried this request
        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        // If already refreshing, queue this request
        if (isRefreshing.current) {
          return new Promise((resolve, reject) => {
            failedQueue.current.push({ resolve, reject });
          }).then(newToken => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }).catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing.current = true;

        try {
          const currentToken = localStorage.getItem('token');
          if (!currentToken) {
            throw new Error('No token available');
          }

          const response = await axios.post(`${API}/auth/refresh`, {}, {
            headers: { 'Authorization': `Bearer ${currentToken}` }
          });
          
          const { access_token } = response.data;
          localStorage.setItem('token', access_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          
          processQueue(null, access_token);
          
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          return axios(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          // Clear auth state on refresh failure
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          return Promise.reject(refreshError);
        } finally {
          isRefreshing.current = false;
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [processQueue]);

  // Fetch user on token change
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      // Token invalid or expired - clear auth state
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const hasAccess = (department) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'manager') return true;
    // member: access only to their own department
    return user.department === department;
  };

  const isAdmin = () => user?.role === 'admin';
  // dept lead is a DepartmentMembership tag, not a workspace role —
  // check via myDeptMemberships in RfpContext for fine-grained access
  const isDeptHead = () => user?.role === 'admin' || user?.role === 'manager';

  // Proactive token refresh: every 6 days (token lasts 7 days)
  useEffect(() => {
    if (!token || !user) return;

    const refreshToken = async () => {
      try {
        const response = await axios.post(`${API}/auth/refresh`);
        const { access_token } = response.data;
        localStorage.setItem('token', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        setToken(access_token);
        console.log('Token refreshed proactively');
      } catch (error) {
        console.error('Proactive token refresh failed:', error);
      }
    };

    // Refresh token every 6 days
    const refreshInterval = setInterval(refreshToken, 6 * 24 * 60 * 60 * 1000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, [token, user]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      hasAccess,
      isAdmin,
      isDeptHead,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
